/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { ReactNode, useCallback, useContext, useMemo } from 'react';

import { Select as MuiSelect, SelectProps as MuiSelectProps, MenuItem, FormControl, FormControlProps, FormHelperText, InputLabel, SelectChangeEvent, InputLabelProps, OutlinedInput, Box, Typography, styled, selectClasses, formLabelClasses, outlinedInputClasses, inputBaseClasses } from '@mui/material';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { IFormState, IFieldInputProps, useFormErrorBoundary } from '@ngt/forms-core';
import SelectIcon from './SelectIcon';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { IAdditionalFormInputProps } from './Input';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


export interface ISelectItem<TId = any> {
    id?: TId,
    name?: string | ReactNode;
    description?: string;
}

type SelectProps<TId = any> = Partial<Omit<MuiSelectProps, 'children' | 'multiple' | 'value'>> & IFieldInputProps<TId, any, FormError, TId> & IAdditionalFormInputProps

export interface ISelectProps<TId = any> extends Omit<SelectProps<TId>, 'margin' | 'onChange'> {
    nullOption?: boolean;
    label?: string;
    FormControlProps?: FormControlProps;
    InputLabelProps?: InputLabelProps;
    onChange?: (event: SelectChangeEvent<any>, child: React.ReactNode, selectedItem: any) => void;
    options?: ISelectItem<TId>[];
    helperText?: ReactNode;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledFormControl = styled(FormControl)(({theme, margin}) => ({
    marginTop: margin === 'normal' ?
        theme.spacing(2) :
        margin === 'dense' ?
            theme.spacing(1) :
            theme.spacing(0),
    marginBottom: margin === 'normal' ?
        theme.spacing(1) :
        margin === 'dense' ?
            theme.spacing(0.5) :
            theme.spacing(0),
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0)
    },
    [`& .${selectClasses.select}`]: {
        padding: theme.spacing(2, 2)
    },
    [`& .${formLabelClasses.root}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(2)}) scale(1)`
    },
    [`& label.${formLabelClasses.focused}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& label.${formLabelClasses.filled}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& fieldset.${outlinedInputClasses.notchedOutline}`]: {
        padding: theme.spacing(0, 1),

        '& legend': {
            fontSize: '1em',
            '& span': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1)
            }
        }
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const Select = <TId = any>({
    FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
    label,
    FormControlProps,
    onChange,
    options,
    fullWidth,
    nullOption,
    InputLabelProps,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    hasErrors,
    errors,
    errorType,
    helperText,
    ...rest
}: ISelectProps<TId>) => {
    const onChangeCombined = useCallback((event: SelectChangeEvent<{ name: string | undefined; value: number; }>, child: React.ReactNode) => {

        const selectedItem = options?.find(x => x.id?.toString() === event.target.value?.toString())
        
        if (onChange) {
            onChange(event, child, selectedItem);
        }

        const newValue = !!selectedItem?.id ? 
            selectedItem?.id : 
            undefined as any;

        update(
            newValue, 
            true, true, true);
    }, [onChange, update, options]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    const fullWidthToUse = fullWidth !== undefined ? 
        fullWidth :
        FormControlProps?.fullWidth !== undefined ?
            FormControlProps?.fullWidth :
            true;

    return (
        <StyledFormControl
            {...FormControlProps}
            error={hasErrors || FormControlProps?.error}
            fullWidth={fullWidthToUse}
            disabled={rest.disabled}
            sx={{...sx, ...errorStyling}}
        >
            <InputLabel {...InputLabelProps} id={`${propertyPath}-label`}>{label}</InputLabel>
            <MuiSelect
                {...rest as any}
                labelId={`${propertyPath}-label`}
                inputProps={{
                    ...inputProps,
                    onBlur: onBlurCombined,
                    onFocus: onFocusCombined
                }}
                input={InputLabelProps?.shrink ? <OutlinedInput notched label={label} disabled={rest.disabled} />  : undefined}
                fullWidth={fullWidthToUse}
                label={label}
                value={value ?? ''}
                IconComponent={SelectIcon}
                onChange={onChangeCombined}
                renderValue={
                    rest.renderValue ?? ((id?: any) => {
                        const item = options?.find(x => x.id?.toString() === id?.toString());

                        if (!item) {
                            return null;
                        }
                        
                        return (
                            <Box>
                                
                                {item.name}
                                {item.description && (
                                    <Typography variant="caption">
                                        {" "}&ndash; {item.description}
                                    </Typography>
                                )}
                            </Box>
                        )
                    })
                }
            >
                {
                    (nullOption !== false) && (
                        <MenuItem
                            value={''}
                        >
                            &nbsp;
                        </MenuItem>
                    )
                }
                {
                    options?.map(item => {
                        return (
                            <MenuItem
                                key={item.id?.toString()}
                                value={item.id?.toString()}
                            >
                                <Box>
                                    {item.name}
                                    {
                                        item.description && (
                                            <Typography variant="caption">
                                                {" "}&ndash; {item.description}
                                            </Typography>
                                        )
                                    }
                                </Box>
                            </MenuItem>
                        );
                    })
                }
            </MuiSelect>
            {
                (hasErrors || helperText) && (
                    <FormHelperText>
                        {helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{helperText}</span>)} 
                        {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                    </FormHelperText>
                )
            }
        </StyledFormControl>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Select;
